// ── Forzar la creación de un fichaje rechazado por la hora de entrada ─────
// CONTEXTO (verificado dos veces en producción):
// POST /api/event aplica la regla enforceStartHour del trabajador comparando
// contra `Workhours.findOne({ where: { workerId, dayOfWeek } })` SIN filtrar
// por versión de horario, así que coge un tramo ANTIGUO.
//   · Trabajador #194: lunes viejo 08:30 vs vigente 08:00 → las entradas de
//     los lunes a las 08:00 se rechazan.
//   · Trabajador #192: lo mismo los martes (viejo 08:30).
// El backend es del compañero y NO se toca. El cliente tampoco puede elegir
// otra hora: la hora ES el dato.
//
// Única salida real desde el panel: desactivar un instante el control del
// trabajador, crear el fichaje y volver a activarlo. Eso NO se hace nunca de
// forma silenciosa — es siempre una acción EXPLÍCITA del admin, y solo de
// quien puede gestionar a ese trabajador (canManageWorker), nunca de un
// trabajador sobre sí mismo (si no, cualquiera se saltaría su propio control).
//
// OJO con el PUT /api/company/person/:id: ignora en silencio algunos campos
// (isLoginEnabled, wantsGPSemails — ver services/api.js). `enforceStartHour`
// SÍ está en la whitelist del handler, pero justo por ese precedente el flag
// se RELEE del trabajador después de restaurarlo: no nos fiamos del 200 OK.
//
// Módulo PURO: recibe las llamadas al API inyectadas, así que la secuencia
// (desactivar → crear → restaurar → verificar) se testea sin red y sin React.
// Testeado en src/utils/__tests__/forceStartHour.test.js.
// Gemelo en la app: TemControlApp/src/lib/forceStartHour.js — si se cambia una
// regla aquí, cambiarla allí (no está bajo el guard de check:shared porque
// cada repo lo coloca en su carpeta de lógica pura).

import { canManageWorker } from '../lib/permissions';

// Los flags del backend viajan como boolean, 0/1 o "true"/"false".
const flagOn = (v) => v === true || v === 1 || v === '1'
  || (typeof v === 'string' && v.trim().toLowerCase() === 'true');

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const msgOf = (err, fallback = 'error desconocido') => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  return err.message || fallback;
};

// ¿Puede ESTE usuario forzar el fichaje de ESTE trabajador?
// Dos condiciones: gestiona al trabajador Y no es él mismo.
export const canForceStartHour = (user, worker) => {
  if (!user || !worker) return false;
  if (sameId(user.id, worker.id)) return false;
  return canManageWorker(user, worker);
};

// Explicación + pregunta para la confirmación. Dice las tres cosas que el
// admin necesita: por qué ha fallado, qué va a pasar en la ficha del
// trabajador y que el control se vuelve a dejar como estaba.
export const forceStartHourExplanation = ({ workerName } = {}) => {
  const who = workerName ? ` de ${workerName}` : ' del trabajador';
  return `El servidor ha rechazado el fichaje porque la cuenta${who} tiene ACTIVADO el control de `
    + 'hora de entrada, y lo está comparando con una versión ANTIGUA de su horario (limitación '
    + 'conocida del backend: no filtra por versión de jornada). La hora pedida no se toca: es el dato.\n\n'
    + 'Si fuerzas, se desactivará ese control un instante, se creará el fichaje y se volverá a dejar '
    + 'como estaba. Se comprobará que ha vuelto a su sitio.';
};

// ── Ejecución de una lista de pasos ───────────────────────────────────────
// `steps` = [{ label: 'la entrada', run: () => createEvent(...) }, ...].
// Se paran en el PRIMER fallo y se informa de lo que sí se creó: un par
// entrada/salida a medias tiene que verse, no quedar escondido tras un error
// genérico. `rest` son los pasos que quedaron sin ejecutar (el fallido
// incluido), para poder reintentarlos tal cual al forzar.
export const runSteps = async (steps = []) => {
  const list = Array.isArray(steps) ? steps : [];
  const created = [];
  for (let i = 0; i < list.length; i++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await list[i].run();
      created.push(list[i].label);
    } catch (error) {
      return { created, failed: { label: list[i].label, error }, rest: list.slice(i) };
    }
  }
  return { created, failed: null, rest: [] };
};

const joinLabels = (labels) => {
  const l = labels.filter(Boolean);
  if (!l.length) return '';
  if (l.length === 1) return l[0];
  return `${l.slice(0, -1).join(', ')} y ${l[l.length - 1]}`;
};

// ── Secuencia completa ────────────────────────────────────────────────────
// 1. LEE el valor original del flag (NO se asume `true`).
// 2. PUT enforceStartHour = false.
// 3. Crea los eventos, en orden, parando en el primer fallo.
// 4. En un `finally` de verdad: PUT enforceStartHour = <original>. Una sola
//    vez al final, aunque sean varios eventos.
// 5. RELEE al trabajador y comprueba que el flag volvió. Si no volvió (o no
//    se puede comprobar), el resultado sale con nivel 'error'/'warning' y un
//    mensaje que manda al admin a arreglarlo A MANO en la ficha.
//
// Parámetros inyectados:
//   · readWorker()            → promesa con la ficha fresca (o null).
//   · setEnforceStartHour(v)  → promesa con el PUT.
//   · steps                   → ver runSteps.
//   · alreadyCreated          → etiquetas creadas ANTES de forzar (p. ej. el
//                               inicio del tramo, que sí pasó).
//
// NUNCA lanza: devuelve siempre un resultado que la pantalla sabe pintar.
export const runWithStartHourDisabled = async ({
  worker, readWorker, setEnforceStartHour, steps = [], alreadyCreated = [],
} = {}) => {
  const name = worker?.name || 'el trabajador';
  const preCreated = Array.isArray(alreadyCreated) ? alreadyCreated.filter(Boolean) : [];

  // 1. Valor ORIGINAL del flag. Primero de la ficha fresca; si no se puede
  //    leer, del worker que tenemos en pantalla; y si tampoco, `true`: solo
  //    se llega aquí después de un rechazo con enforceStartHour = TRUE, y
  //    dejar el control apagado por una suposición no es una opción.
  let original = null;
  if (typeof readWorker === 'function') {
    try {
      const fresh = await readWorker();
      if (fresh && fresh.enforceStartHour !== undefined) original = flagOn(fresh.enforceStartHour);
    } catch {
      original = null;
    }
  }
  if (original === null && worker && worker.enforceStartHour !== undefined) {
    original = flagOn(worker.enforceStartHour);
  }
  if (original === null) original = true;

  // 2. Desactivar. Si esto falla no se ha tocado nada: se corta aquí.
  try {
    await setEnforceStartHour(false);
  } catch (error) {
    return {
      ok: false,
      level: 'error',
      original,
      created: preCreated,
      failed: { label: null, error },
      restored: true,
      restoreError: null,
      message: `No se ha podido desactivar temporalmente el control de hora de entrada de ${name} `
        + `(${msgOf(error)}). No se ha creado nada y su ficha NO se ha modificado.`,
    };
  }

  // 3. Crear.
  let run = { created: [], failed: null, rest: [] };
  try {
    run = await runSteps(steps);
  } catch (error) {
    run = { created: [], failed: { label: null, error }, rest: [] };
  }

  // 4. Restaurar SIEMPRE, una sola vez.
  let restoreError = null;
  try {
    await setEnforceStartHour(original);
  } catch (error) {
    restoreError = error;
  }

  // 5. Verificar releyendo la ficha. null = no se ha podido comprobar.
  let restored = null;
  if (typeof readWorker === 'function') {
    try {
      const after = await readWorker();
      if (after && after.enforceStartHour !== undefined) {
        restored = flagOn(after.enforceStartHour) === original;
      }
    } catch {
      restored = null;
    }
  }
  if (restored === null && restoreError) restored = false;

  const created = [...preCreated, ...run.created];
  const ok = !run.failed && created.length > 0;

  // Mensaje: primero qué pasó con los fichajes, luego qué pasó con el flag.
  const parts = [];
  if (run.failed) {
    parts.push(created.length
      ? `Se creó ${joinLabels(created)}, pero FALLÓ ${run.failed.label || 'el fichaje'}: ${msgOf(run.failed.error)}.`
      : `No se pudo crear ${run.failed.label || 'el fichaje'}: ${msgOf(run.failed.error)}.`);
  } else if (created.length) {
    parts.push(`Creado ${joinLabels(created)}.`);
  }

  if (restored === true) {
    parts.push(original
      ? `El control de hora de entrada de ${name} se ha vuelto a ACTIVAR (comprobado).`
      : `El control de hora de entrada de ${name} sigue desactivado, como estaba (comprobado).`);
  } else if (restored === false) {
    parts.push(`¡ATENCIÓN! NO se ha podido devolver el control de hora de entrada de ${name} a su `
      + `estado original (${original ? 'activado' : 'desactivado'})`
      + `${restoreError ? `: ${msgOf(restoreError)}` : ''}. `
      + 'Entra AHORA en su ficha y déjalo como debe estar A MANO.');
  } else {
    parts.push(`No se ha podido COMPROBAR si el control de hora de entrada de ${name} ha vuelto a su `
      + 'sitio. Revísalo en su ficha antes de seguir.');
  }

  let level = 'success';
  if (restored === false) level = 'error';
  else if (run.failed) level = 'error';
  else if (restored === null) level = 'warning';

  return {
    ok,
    level,
    original,
    created,
    failed: run.failed,
    restored,
    restoreError,
    message: parts.join(' '),
  };
};
